import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation } from '@reach/router';
import pt from 'prop-types';
import Text from '../text';
import Checkbox from '../checkbox';
import { combine } from '../../../helpers/styles';
import Icon from '../icon';
import { formatFilterLabel, getCopyWithFallback } from '../../../helpers/copy';
import * as styles from './filterSet.module.scss';
import Radiobutton from '../radiobutton';
import { buildSearchParams } from '../../../helpers/filters';
import { MicrocopyContext } from '../../../context/microcopy.context';
import { getCopy } from '../../../helpers/copy';
import { useLocale } from '../../../context/locale.context';
import { getCountryName } from '../../../utils/constants';

const FilterSet = ({
  label,
  visibleFilters,
  queryFilters,
  withButtonMargin,
  withTopBorder,
  isRadio,
  openAtStart,
  onChange,
  isDynamic,
  type,
  award = '',
  year,
}) => {
  const microcopy = useContext(MicrocopyContext);
  const { locale } = useLocale();
  const location = useLocation();
  const ref = useRef();
  const [isOpen, setIsOpen] = useState(openAtStart);
  const [maxHeight, setMaxHeight] = useState('none');
  const [filters, setFilters] = useState([]);

  const getFilterOptionLabel = (option, filterId) => {
    const isWdi = location.pathname.includes('wdi');

    // region is continent in wdi ranking
    filterId = (filterId === 'continent' ? 'region' : filterId)
      .toLowerCase()
      .replace(' ', '');

    // don't translate categories disciplines, industries, sectors or fields of work (nor countries if wdi)
    if (filterId === 'industry' || (filterId === 'country' && isWdi)) {
      return null;
    }

    if (filterId === 'sectors') {
      return getCopyWithFallback(
        `filters.sectors.${option.id}`,
        microcopy,
        option.name
      );
    }

    if (filterId === 'fieldsofwork') {
      return getCopyWithFallback(
        `filters.fieldsofwork.${option.id}`,
        microcopy,
        option.name
      );
    }

    if (filterId === 'categories') {
      return getCopyWithFallback(
        `filters.category.${option.id}${option.type ? `.${option.type}` : ''}`,
        microcopy,
        option.name
      );
    }

    if (filterId === 'disciplines') {
      return getCopyWithFallback(
        `filters.discipline.${option.id}`,
        microcopy,
        option.name
      );
    }

    // use Intl.DisplayNames to translate country names
    if (['countries', 'country'].includes(filterId)) {
      return getCountryName(locale, option.id);
    }

    // handle special awards
    if (filterId === 'awards') {
      if (option.id === 9999) {
        return getCopy('filters.awards.2.special', microcopy);
      } else if (option.id === 7777) {
        return getCopy('filters.awards.1.special', microcopy);
      }
    }

    if (filterId === 'time') {
      // options 0 (this year) and 1(last year) have different microcopies for jurors
      const hasCustomMicrocopy =
        type === 'juror' && (option.id == 0 || option.id == 1) && award;

      // if the option is this year or last year, we need to replace %YEAR% with the actual year (minus 0 for current year or minus 1 for last year)
      return getCopy(
        `filters.time.${option.id}${type ? `.${type}` : ''}${
          hasCustomMicrocopy ? `.${award}` : ''
        }`,
        microcopy
      ).replace('%YEAR%', year - option.id);
    }

    if (filterId === 'profiletypes' || filterId === 'profiletype') {
      return getCopy(
        `filters.profiletypes.${option.id}${type ? `.${type}` : ''}`,
        microcopy
      );
    }

    return getCopy(
      `filters.${filterId}.${option.id}`.toLowerCase().replace(' ', ''),
      microcopy
    );
  };

  useEffect(() => {
    // filter out filters that should be hidden and check them if they should be checked
    const _filters = visibleFilters
      .filter((filter) => !filter.hide)
      .map((filter) => ({
        ...filter,
        checked: queryFilters?.includes(filter.id),
      }));

    // sort countries by localized name or by sort order
    if (['countries', 'country'].includes(label)) {
      _filters.sort((a, b) =>
        getCountryName(locale, a.id).localeCompare(getCountryName(locale, b.id))
      );
    } else {
      _filters.sort((a, b) => a.sort - b.sort);
    }

    setFilters(_filters);
  }, [queryFilters, visibleFilters]);

  const handleFilterClick = onChange
    ? (filter) => {
        const newFilters = filters.map((f) =>
          f.id === filter.id ? { ...f, checked: !filter.checked } : f
        );
        setFilters(newFilters);
      }
    : () => {};

  useEffect(() => {
    if (onChange) {
      onChange(filters.filter((el) => el.checked).map((el) => el.id));
    }
  }, [filters]);

  const style = {
    transition: 'max-height 350ms ease-in-out',
    overflow: 'hidden',
    // For the collapse animation to work we need a fixed max-height,
    // therefore we simply pass the actual height of the element when the user collapses it (isOpen === false)
    maxHeight:
      maxHeight === 'none' && !isOpen ? ref.current?.clientHeight : maxHeight,
  };

  useEffect(() => {
    if (!isOpen) {
      setMaxHeight('0');
    } else {
      setMaxHeight(
        ref.current?.clientHeight ? `${ref.current?.clientHeight}px` : 'none'
      );
    }
  }, [isOpen]);

  const activeFilterCount = filters.filter((filter) => filter.checked).length;

  const Input = ({ children, filter }) => {
    if (!onChange) {
      const searchParams = buildSearchParams({
        filterKey: isDynamic ? 'dynamicFilterId' : label,
        filter,
        isSingleValue: isRadio,
      }).toString();

      return (
        <a
          href={searchParams ? `?${searchParams}` : location?.pathname}
          className={styles.filter}
          data-test={`filter-${label}-${filter.id}`}
        >
          {children}
        </a>
      );
    }

    return children;
  };

  return filters.length > 0 && filters.filter((el) => !el.hide).length > 0 ? (
    <div
      className={combine(
        styles.filterSet,
        isOpen && styles.withMargin,
        withTopBorder && styles.withBorder
      )}
      data-test={`filterset-${label}`}
    >
      <button onClick={() => setIsOpen(!isOpen)} className={styles.header}>
        <Text as="span" type="metanavi" variant="semibold">
          {label !== 'sort'
            ? formatFilterLabel(
                getCopy(
                  `filters.${label}`.toLowerCase().replace(' ', ''),
                  microcopy
                ),
                activeFilterCount
              )
            : getCopy(
                `filters.${label}`.toLowerCase().replace(' ', ''),
                microcopy
              ).toUpperCase()}
        </Text>
        <Icon
          name={isOpen ? 'chevron-up' : 'chevron-down'}
          className={combine(
            styles.icon,
            withButtonMargin && styles.withMargin
          )}
          data-test={`filter-${label}`}
        />
      </button>
      <div
        style={style}
        onTransitionEnd={() => {
          if (isOpen) {
            // To avoid problems when resizing the screen we simply reset max-height to default after ended transition
            setMaxHeight('none');
          }
        }}
      >
        <div className={styles.filtersContainer} ref={ref}>
          {filters.map((filter) =>
            isRadio ? (
              <Input filter={filter}>
                <Radiobutton
                  label={getFilterOptionLabel(filter, label) || filter.name}
                  key={filter.id}
                  defaultChecked={filter.checked}
                  className={styles.isRadio}
                  disabled={filter.disabled}
                  onClick={() => handleFilterClick(filter)}
                  isLink
                />
              </Input>
            ) : (
              <Input filter={filter}>
                <Checkbox
                  label={getFilterOptionLabel(filter, label) || filter.name}
                  key={filter.id}
                  defaultChecked={filter.checked}
                  disabled={filter.disabled}
                  onClick={() => handleFilterClick(filter)}
                  isLink
                />
              </Input>
            )
          )}
        </div>
      </div>
    </div>
  ) : null;
};

FilterSet.propTypes = {
  label: pt.string.isRequired,
  visibleFilters: pt.arrayOf(
    pt.shape({
      name: pt.string,
      id: pt.oneOfType([pt.string, pt.number]),
    })
  ).isRequired,
  queryFilters: pt.arrayOf(pt.string),
  withButtonMargin: pt.bool,
  withTopBorder: pt.bool,
  openAtStart: pt.bool,
  type: pt.string,
};

FilterSet.defaultProps = {
  withButtonMargin: false,
  withTopBorder: false,
  openAtStart: true,
  type: '',
};

export default FilterSet;
